import * as Sentry from '@sentry/react';
import type { SeverityLevel } from '@sentry/react';
import { networkNames } from '@soundxyz/utils/constants/constants';
import type { Primitive } from '@soundxyz/utils/constants/types';
import { ModalContext, ModalType } from '@context/modal';
import type { OpenToastProps } from '@context/toast';
import { ToastContext } from '@context/toast';
import { useWalletRequest } from '@context/wallet-request';
import { useStableCallback } from '@hooks/useStableCallback';
import { PRIMARY_CHAIN_ID } from '@utils/constants';

type LogErrorProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  toast?: OpenToastProps | string;
  capture?: boolean;
  closeProcessingModal?: boolean;
  indexedTags?: Record<string, Primitive>;
  unindexedExtra?: Record<string, unknown>;
  toastDuration?: number;
  level?: SeverityLevel;
};

export const REQUEST_REJECTED_TOAST_MESSAGE = 'Request rejected in wallet.';

/**
 * @deprecated Migrate to `@hooks/useLogErrorNew`
 */
export function useLogError() {
  const { openToast } = ToastContext.useContainer();
  const { setRequestRejected } = useWalletRequest();
  const { closeModalByType } = ModalContext.useContainer();

  const logError = useStableCallback(
    /**
     * @deprecated Migrate to `@hooks/useLogErrorNew`
     */
    ({
      error,
      toast,
      capture = true,
      closeProcessingModal = false,
      indexedTags,
      unindexedExtra,
      toastDuration = 5000,
      level,
    }: LogErrorProps) => {
      // eslint-disable-next-line no-console
      console.error(error);

      const errMessage = typeof error !== 'string' ? error?.message ?? '' : '';

      // https://eips.ethereum.org/EIPS/eip-1193#provider-errors
      if (
        error?.code === 4001 ||
        !!errMessage.match(/(user|signing transaction was|signing was) (rejected|denied|cancel)/gi)
      ) {
        closeModalByType(ModalType.PROCESSING);
        closeModalByType(ModalType.PROCESSING_UPLOAD_JOURNEY);
        setRequestRejected(true);
        openToast({
          text: REQUEST_REJECTED_TOAST_MESSAGE,
          variant: 'error',
          duration: toastDuration,
        });
        // Reset request rejected state
        setTimeout(() => {
          setRequestRejected(false);
        }, toastDuration);
        return;
      }

      if (error?.message?.includes('Unsupported chain id')) {
        openToast({
          text: `Please connect your wallet to ${networkNames[PRIMARY_CHAIN_ID]}`,
          variant: 'error',
          duration: toastDuration,
        });
        return;
      }

      if (capture) {
        Sentry.captureException(error instanceof Error ? error : new Error(JSON.stringify(error)), {
          tags: indexedTags,
          extra: unindexedExtra,
          level,
        });
      }

      if (toast) {
        if (typeof toast === 'string') {
          openToast({
            text: toast,
            variant: 'error',
            duration: toastDuration,
          });
        } else {
          openToast({
            variant: 'error',
            duration: toastDuration,
            ...toast,
          });
        }
      }

      if (closeProcessingModal) {
        closeModalByType(ModalType.PROCESSING);
        closeModalByType(ModalType.PROCESSING_UPLOAD_JOURNEY);
      }
    },
  );

  return logError;
}
